// S9 Routing (REQ-6.6): who is responsible for what, in words the AI reads when it routes a report.
// Editors and Admins only (the server refuses anyone else; poc CT-008).
import type { AppView, PersonRef } from '@feedback/shared/contracts/apps';
import { Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { api, seg } from '@/lib/api';
import { errorText, fieldError, fieldErrors } from '../../lib/admin-api';
import { Button, Field, Input, Notice, Panel } from '../../ui/primitives';
import { appPath, useAppWrite } from '../app-data';
import { PeopleChips, PersonSearch } from '../pickers';

type RoutingRow = AppView['routing'][number];

function RoutingItem({ app, row }: { app: AppView; row: RoutingRow }) {
  const readOnly = !app.can.manageSettings;
  const [description, setDescription] = useState(row.description);
  const update = useAppWrite(
    app.id,
    (d: string) => api.patch<AppView>(appPath(app.id, `/routing/${seg(row.id)}`), { description: d }),
    'Routing saved',
  );
  const remove = useAppWrite(
    app.id,
    () => api.delete<AppView>(appPath(app.id, `/routing/${seg(row.id)}`)),
    'Routing removed',
  );
  return (
    <li className="flex flex-col gap-2 rounded-md border border-border p-3 sm:flex-row sm:items-end">
      <div className="text-sm font-medium sm:w-40 sm:pb-2.5">{row.person.displayName} →</div>
      <form
        className="flex flex-1 flex-wrap items-end gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          update.mutate(description.trim());
        }}
      >
        <Field
          label={`What ${row.person.displayName} is responsible for`}
          className="min-w-0 flex-1"
          error={update.isError ? errorText(update.error) : undefined}
        >
          {({ id, invalid }) => (
            <Input
              id={id}
              aria-invalid={invalid}
              value={description}
              disabled={readOnly}
              onChange={(e) => setDescription(e.target.value)}
            />
          )}
        </Field>
        {readOnly ? null : (
          <>
            <Button
              type="submit"
              busy={update.isPending}
              disabled={description.trim() === row.description || !description.trim()}
            >
              Save
            </Button>
            <Button
              variant="ghost"
              aria-label={`Remove routing for ${row.person.displayName}`}
              busy={remove.isPending}
              onClick={() => remove.mutate(undefined)}
            >
              <Trash2 aria-hidden="true" className="size-4" />
            </Button>
          </>
        )}
      </form>
      {remove.isError ? <p className="text-xs text-danger">{errorText(remove.error)}</p> : null}
    </li>
  );
}

export function RoutingSection({ app }: { app: AppView }) {
  const [person, setPerson] = useState<PersonRef | null>(null);
  const [description, setDescription] = useState('');
  const create = useAppWrite(
    app.id,
    (body: { personId: string; description: string }) => api.post<AppView>(appPath(app.id, '/routing'), body),
    'Routing added',
  );
  const errors = fieldErrors(create.error);
  return (
    <Panel
      title="Routing"
      description="Tell the AI who looks after what, for example “bugs in payments”. It uses this to pick the responsible person."
    >
      <ul className="flex flex-col gap-2">
        {app.routing.length === 0 ? (
          <li className="text-sm text-muted">No routing yet: new tickets go to the fallback owner.</li>
        ) : null}
        {app.routing.map((row) => (
          <RoutingItem key={`${row.id}:${row.updatedAt}`} app={app} row={row} />
        ))}
      </ul>
      {app.can.manageSettings ? (
        <form
          className="mt-4 flex flex-col gap-3 border-t border-border pt-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (!person) return;
            create.mutate(
              { personId: person.principalId, description: description.trim() },
              {
                onSuccess: () => {
                  setPerson(null);
                  setDescription('');
                },
              },
            );
          }}
        >
          <h3 className="text-sm font-semibold">Add routing</h3>
          {person ? (
            <PeopleChips people={[person]} onRemove={() => setPerson(null)} />
          ) : (
            <PersonSearch
              label="Person (an Editor)"
              onPick={(p) => setPerson({ principalId: p.principalId, displayName: p.displayName })}
            />
          )}
          {fieldError(errors, 'personId') ? (
            <p className="text-xs font-medium text-danger">{fieldError(errors, 'personId')}</p>
          ) : null}
          <Field label="What they’re responsible for" error={fieldError(errors, 'description')}>
            {({ id, invalid }) => (
              <Input
                id={id}
                aria-invalid={invalid}
                value={description}
                placeholder="bugs in payments"
                onChange={(e) => setDescription(e.target.value)}
              />
            )}
          </Field>
          {create.isError && Object.keys(errors).length === 0 ? (
            <Notice tone="danger">{errorText(create.error)}</Notice>
          ) : null}
          <div>
            <Button type="submit" variant="primary" busy={create.isPending} disabled={!person || !description.trim()}>
              <Plus aria-hidden="true" className="size-4" /> Add routing
            </Button>
          </div>
        </form>
      ) : null}
    </Panel>
  );
}
